import { 
  LayoutDashboard, 
  Users, 
  Store, 
  FileText, 
  Settings,
  CreditCard,
  BarChart,
  Briefcase,
  UserCircle,
  Award,
  Brain,
  Shield,
  Target,
  TrendingUp,
  ShoppingBag,
  Smartphone,
  Building2,
  Package,
  Tags,
  Truck,
  ShoppingCart,
  Receipt,
  Gift,
  Globe,
  Sparkles,
  BarChart3,
  Circle,
  Ticket,
  Table,
  Dices,
  Box,
  Layers,
  List,
  ListChecks,
  Megaphone,
  PieChart,
  History,
  ScrollText,
} from "lucide-react";
import { config } from "@/config";

const BLOCKCHAIN_EXPLORER_ITEM = {
  name: "Blockchain Explorer",
  href: config.BLOCKCHAIN_EXPLORER_URL,
  icon: Globe,
  external: true,
} as const;

export interface SidebarItem {
  name: string;
  /** Optional when the item only expands nested children */
  href?: string;
  icon: any;
  /** Open href in a new browser tab (external URL) */
  external?: boolean;
  /** Nested menu items shown under this parent */
  children?: SidebarItem[];
  /** Open an in-app modal instead of navigating (e.g. "perkpoint-settings") */
  opensModal?: "perkpoint-settings";
}

export interface SidebarGroup {
  label?: string;
  items: SidebarItem[];
}

export interface SidebarConfig {
  appName: string;
  appShortName: string;
  appColor: string;
  groups: SidebarGroup[];
}

export const sidebarConfigs: Record<string, SidebarConfig> = {
  accesspoint: {
    appName: "Access Point",
    appShortName: "AP",
    appColor: "#3B82F6",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/accesspoint", icon: LayoutDashboard },
          { name: "Programs", href: "/accesspoint/programs", icon: Users },
          { name: "Merchants", href: "/accesspoint/merchants", icon: Store },
          {
            name: "Merchant App",
            href: "https://retail.staging.loyyal.io/",
            icon: Smartphone,
            external: true,
          },
          // { name: "Accounting Entries", href: "/accesspoint/accounting-entry", icon: FileText },
          { name: "Invoices", href: "/accesspoint/invoices", icon: Receipt },
          // { name: "Analytics", href: "/accesspoint/analytics", icon: TrendingUp },
          { name: "Reports", href: "/accesspoint/reports-insights", icon: BarChart },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      },
      {
        label: "Extras",
        items: [
          { name: "Jobs", href: "/accesspoint/jobs", icon: Briefcase },
          { name: "Users & Roles", href: "/users", icon: UserCircle },
          { name: "Clients", href: "/accesspoint/clients", icon: Building2 },
          { name: "Settings", href: "/settings", icon: Settings },
        ]
      }
    ]
  },
  gamepoint: {
    appName: "Game Point",
    appShortName: "GP",
    appColor: "#EA580C",
    groups: [
      {
        items: [
          { name: "Analytics", href: "/gamepoint/analytics", icon: BarChart3 },
          { name: "Spin a Wheel", href: "/gamepoint/spin-a-wheel", icon: Circle },
          { name: "Scratch Card", href: "/gamepoint/scratch-card", icon: Ticket },
          { name: "Slot Machine", href: "/gamepoint/slot-machine", icon: Dices },
          { name: "Mystery Box", href: "/gamepoint/mystery-box", icon: Box },
          { name: "Quiz", href: "/gamepoint/quiz", icon: ListChecks },
          { name: "Report", href: "/gamepoint/report", icon: Table },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ],
      },
    ],
  },
  perkpoint: {
    appName: "Perk Points",
    appShortName: "PP",
    appColor: "#22C55E",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/perkpoint", icon: LayoutDashboard },
          {
            name: "Members",
            icon: Users,
            children: [
              {
                name: "Insights",
                href: "/perkpoint/member/insight",
                icon: Brain,
              },
              {
                name: "Segments",
                href: "/perkpoint/member/segments",
                icon: PieChart,
              },
              {
                name: "Members",
                href: "/perkpoint/member/all",
                icon: List,
              },
              {
                name: "Activities",
                href: "/perkpoint/member/history",
                icon: History,
              },
            ],
          },
          {
            name: "Program",
            icon: Building2,
            children: [
              {
                name: "Configure",
                href: "/perkpoint/program/setup",
                icon: Settings,
              },
              {
                name: "Tier",
                href: "/perkpoint/rewards/tiers",
                icon: Layers,
              },
              {
                name: "Rules Engine",
                href: "/perkpoint/rule-engine",
                icon: ListChecks,
              },
            ],
          },
          {
            name: "Engagement",
            icon: Megaphone,
            children: [
              {
                name: "Campaigns",
                href: "/perkpoint/rewards/campaigns",
                icon: Target,
              },
            ],
          },
          { name: "Outlets", href: "/perkpoint/outlets", icon: Store },
          { name: "Reports", href: "/perkpoint/report", icon: FileText },
          {
            name: "Audit History",
            href: "/perkpoint/audit/history",
            icon: ScrollText,
          },
          { name: "Fraud Detection", href: "/perkpoint/fraud-detection", icon: Shield },
          { name: "Settings", icon: Settings, opensModal: "perkpoint-settings" },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      }
    ]
  },
  xpandpoint: {
    appName: "Xpand Point",
    appShortName: "XP",
    appColor: "#1EAEDB",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/xpandpoint", icon: LayoutDashboard },
          { name: "Program", href: "/xpandpoint/transfer-programs", icon: Building2 },
          { name: "Transfer Partners", href: "/xpandpoint/partners", icon: Award },
          {
            name: "Point Transfer App",
            href: "https://points-transfer.staging.loyyal.io/",
            icon: Smartphone,
            external: true,
          },
          { name: "Transactions", href: "/xpandpoint/transactions", icon: CreditCard },
          // { name: "Accounting Entries", href: "/xpandpoint/accounting-entry", icon: FileText },
          { name: "Invoices", href: "/xpandpoint/invoices", icon: Receipt },
          // { name: "Analytics", href: "/xpandpoint/analytics", icon: BarChart },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      },
      {
        label: "Extras",
        items: [
          { name: "Jobs", href: "/xpandpoint/jobs", icon: Briefcase },
          { name: "Users & Roles", href: "/xpandpoint/users", icon: UserCircle },
          { name: "Clients", href: "/xpandpoint/clients", icon: Building2 },
          { name: "Settings", href: "/xpandpoint/settings", icon: Settings },
        ]
      }
    ]
  },
  rewardpoint: {
    appName: "Reward Points",
    appShortName: "RP",
    appColor: "#8B5CF6",
    groups: [
      {
        items: [
          { name: "Programs", href: "/rewardpoint/programs", icon: Award },
          { name: "Brands", href: "/rewardpoint/brands", icon: Building2 },
          { name: "Products", href: "/rewardpoint/products", icon: Package },
          { name: "Platform Categories", href: "/rewardpoint/categories", icon: Tags },
          { name: "Redemption Merchants", href: "/rewardpoint/redemption-merchants", icon: Store },
          { name: "Shipping Profiles", href: "/rewardpoint/shipping-profiles", icon: Truck },
          { name: "Orders", href: "/rewardpoint/orders", icon: ShoppingCart },
          // { name: "Account Entries", href: "/rewardpoint/accounts", icon: Receipt },
          { name: "Invoices", href: "/rewardpoint/invoices", icon: FileText },
          {
            name: "Storefront",
            href: "https://rewardpoint.burnshop.staging.loyyal.io/",
            icon: ShoppingBag,
            external: true,
          },
          // { name: "Analytics", href: "/rewardpoint/analytics-new", icon: BarChart },
          // { name: "Users", href: "/users", icon: UserCircle },
          // { name: "Settings", href: "/settings", icon: Settings },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      },
      {
        label: "Extras",
        items: [
          { name: "Jobs", href: "/rewardpoint/jobs", icon: Briefcase },
          { name: "Users & Roles", href: "/rewardpoint/users", icon: UserCircle },
          { name: "Clients", href: "/rewardpoint/clients", icon: Building2 },
          { name: "Settings", href: "/rewardpoint/settings", icon: Settings },
        ]
      }
    ]
  },
  giftcard: {
    appName: "Gift OS",
    appShortName: "GC",
    appColor: "#F59E0B",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/giftcard", icon: LayoutDashboard },
          { name: "All Cards", href: "/giftcard/all-giftcards", icon: Gift },
          { name: "Issued Cards", href: "/giftcard/allissuedgiftcards", icon: CreditCard },
          { name: "Redeem Card", href: "/giftcard/redeem", icon: Gift },
          { name: "Campaign", href: "/giftcard/campaign", icon: Target },
          { name: "Brands", href: "/giftcard/brands", icon: Store },
          { name: "Mall", href: "/giftcard/mall", icon: Building2 },
          { name: "Logs", href: "/giftcard/logs", icon: FileText },
          { name: "Settings", href: "/giftcard/settings", icon: Settings },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      }
    ]
  },
  offers: {
    appName: "Offer Point",
    appShortName: "OP",
    appColor: "#E11D48",
    groups: [
      {
        items: [
          { name: "Dashboard", href: "/offers", icon: LayoutDashboard },
          { name: "Programs", href: "/offers/programs", icon: Users },
          { name: "Merchants", href: "/offers/merchants", icon: Store },
          { name: "Recommendation", href: "/offers/recommendation", icon: Sparkles },
          { ...BLOCKCHAIN_EXPLORER_ITEM },
        ]
      }
    ]
  }
};

/**
 * Perk Point sidebar for Loyyal / Perk Point admins. `portfolio` shows only
 * Dashboard + Programs; `manager` shows the full menu scoped to one program.
 * Other roles use `sidebarConfigs.perkpoint` unchanged.
 */
export function perkpointScopedGroups(
  mode: "portfolio" | "manager",
  programId?: string,
): SidebarGroup[] {
  if (mode === "portfolio") {
    return [
      {
        items: [
          { name: "Dashboard", href: "/perkpoint", icon: LayoutDashboard },
          { name: "Programs", href: "/perkpoint/program/setup", icon: Building2 },
        ],
      },
    ];
  }
  const ownProgram = `/perkpoint/program/setup/${programId}`;
  return sidebarConfigs.perkpoint.groups.map((group) => ({
    ...group,
    items: group.items.map((item) => ({
      ...item,
      children: item.children?.map((child) =>
        child.href === "/perkpoint/program/setup"
          ? { ...child, href: ownProgram }
          : child,
      ),
    })),
  }));
}
